<template>
  <div class="min-h-screen bg-linear-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-950 dark:to-gray-900 relative">
    <BreadCrumbs :sessionId="sessionId" />
    <PhaseNavigation />

    <main class="flex-1 w-full pb-32">
      <section class="w-full max-w-7xl mx-auto px-6 py-8">
        <!-- Action Buttons Section -->
        <div class="mb-8 flex items-center justify-between">
          <div class="flex items-center gap-3">
            <!-- Generate New Analysis -->
            <button
              v-if="!riskAnalysis"
              @click="generateNewAnalysis"
              :disabled="isLoading"
              class="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg font-medium transition-colors flex items-center gap-2"
            >
              <template v-if="isLoading">
                <div class="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Generating...
              </template>
              <template v-else>
                Generate Analysis
              </template>
            </button>
          </div>

          <div class="flex items-center gap-3">
            <!-- Re-analyze -->
            <template v-if="riskAnalysis && !isLoading">
              <button
                @click="reanalyzeRiskAnalysis"
                :disabled="isLoading"
                class="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
                </svg>
                Re-analyze
              </button>
            </template>
            <template v-else-if="riskAnalysis && isLoading">
              <button
                disabled
                class="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-700 rounded-lg font-medium opacity-50 cursor-not-allowed"
              >
                <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                Re-analyzing...
              </button>
            </template>

            <!-- View Metadata -->
            <button
              v-if="riskAnalysis?.metadata"
              @click="showMetadataModal = true"
              class="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-medium transition-colors flex items-center gap-2"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              View Details
            </button>

            <!-- Generate Report -->
            <button
              v-if="riskAnalysis && riskAnalysis.status === 'completed'"
              @click="goToReport"
              class="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium transition-colors flex items-center gap-2"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Risk Report
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                </svg>
            </button>
          </div>
        </div>

        <!-- Overall Risk Summary -->
        <div v-if="riskAnalysis" class="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 mb-8">
            <!-- Entity Information -->
            <div v-if="riskAnalysis.metadata?.entityName" class="mb-6 pb-4 border-b border-gray-200 dark:border-gray-700">
              <div class="flex items-center justify-between">
                <div>
                  <h2 class="text-xl font-bold text-gray-900 dark:text-white">{{ riskAnalysis.metadata.entityName }}</h2>
                  <p class="text-gray-600 dark:text-gray-400 capitalize">{{ riskAnalysis.metadata.entityType || 'Unknown' }} Risk Analysis</p>
                </div>
                <div class="text-right">
                  <p class="text-sm text-gray-600 dark:text-gray-400">Last Analyzed</p>
                  <p class="text-sm font-medium text-gray-900 dark:text-white">{{ formatDate(riskAnalysis.metadata.lastAnalyzed) }}</p>
                </div>
              </div>
            </div>
            
            <!-- Risk Metrics Grid -->
            <div class="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
              <div class="text-center">
                <div class="mb-2">
                  <RiskLevelBadge :level="riskAnalysis.summary?.overallRiskLevel || overallRiskLevel" size="large" />
                </div>
                <h3 class="font-semibold text-gray-900 dark:text-white">Overall Risk</h3>
                <p class="text-sm text-gray-600 dark:text-gray-400 capitalize">{{ riskAnalysis.summary?.overallRiskLevel || overallRiskLevel }}</p>
                <p v-if="riskAnalysis.summary?.confidence" class="text-sm text-blue-600 dark:text-blue-400 font-medium mt-1">
                  {{ Math.round(riskAnalysis.summary.confidence * 100) }}% confidence
                </p>
              </div>
              
              <div class="text-center">
                <div class="text-3xl font-bold text-gray-900 dark:text-white mb-1">{{ riskAnalysis.summary?.totalFindings || totalFindings }}</div>
                <h3 class="font-semibold text-gray-900 dark:text-white">Total Findings</h3>
                <p class="text-sm text-gray-600 dark:text-gray-400">Across all categories</p>
              </div>
              
              <div class="col-span-2 text-center">
                <h3 class="font-semibold text-gray-900 dark:text-white mb-3">Findings by Severity</h3>
                <div class="flex items-center justify-center gap-4">
                  <div class="text-center">
                    <div class="px-3 py-1.5 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded-lg text-lg font-bold mb-1">{{ riskAnalysis.summary?.findingsBySeverity?.critical || 0 }}</div>
                    <span class="text-xs text-gray-600 dark:text-gray-400">Critical</span>
                  </div>
                  <div class="text-center">
                    <div class="px-3 py-1.5 bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400 rounded-lg text-lg font-bold mb-1">{{ riskAnalysis.summary?.findingsBySeverity?.high || 0 }}</div>
                    <span class="text-xs text-gray-600 dark:text-gray-400">High</span>
                  </div>
                  <div class="text-center">
                    <div class="px-3 py-1.5 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 rounded-lg text-lg font-bold mb-1">{{ riskAnalysis.summary?.findingsBySeverity?.medium || 0 }}</div>
                    <span class="text-xs text-gray-600 dark:text-gray-400">Medium</span>
                  </div>
                  <div class="text-center">
                    <div class="px-3 py-1.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded-lg text-lg font-bold mb-1">{{ riskAnalysis.summary?.findingsBySeverity?.low || 0 }}</div>
                    <span class="text-xs text-gray-600 dark:text-gray-400">Low</span>
                  </div>
                  <div class="text-center">
                    <div class="px-3 py-1.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-lg text-lg font-bold mb-1">{{ riskAnalysis.summary?.findingsBySeverity?.minimal || 0 }}</div>
                    <span class="text-xs text-gray-600 dark:text-gray-400">Minimal</span>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- Overall Risk Summary -->
            <div v-if="riskAnalysis.overallRiskSummary" class="mb-6">
              <div class="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <div class="flex items-start gap-3">
                  <div class="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center shrink-0 mt-0.5">
                    <svg class="w-5 h-5 text-blue-600 dark:text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
                    </svg>
                  </div>
                  <div class="flex-1">
                    <h4 class="font-semibold text-gray-900 dark:text-white mb-2">Risk Assessment Summary</h4>
                    <p class="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">{{ riskAnalysis.overallRiskSummary }}</p>
                    <div v-if="riskAnalysis.confidence" class="mt-3 flex items-center gap-2">
                      <span class="text-xs font-medium text-gray-600 dark:text-gray-400">Analysis Confidence:</span>
                      <span class="px-2 py-1 bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 rounded text-xs font-semibold">{{ (riskAnalysis.confidence * 100).toFixed(1) }}%</span>
                      <span class="text-xs text-gray-500 dark:text-gray-400">({{ riskAnalysis.confidence.toFixed(3) }})</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- Key Risks Summary -->
            <div v-if="riskAnalysis.keyRisks?.length > 0">
              <h4 class="font-semibold text-gray-900 dark:text-white mb-3">Key Risks ({{ riskAnalysis.keyRisks.length }})</h4>
              <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                <div v-for="(risk, index) in riskAnalysis.keyRisks.slice(0, 6)" :key="index"
                    class="bg-gray-50 dark:bg-gray-900 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
                  <div class="flex items-start justify-between mb-2">
                    <h5 class="font-medium text-gray-900 dark:text-white text-sm">{{ risk.title }}</h5>
                    <RiskLevelBadge :level="risk.severity" size="small" />
                  </div>
                  <p class="text-xs text-gray-600 dark:text-gray-400 line-clamp-2">{{ risk.description }}</p>
                  <div class="flex items-center gap-2 mt-2 text-xs">
                    <span class="text-gray-500">Impact: {{ risk.impact }}</span>
                    <span class="text-gray-400">•</span>
                    <span class="text-gray-500">{{ risk.likelihood }}</span>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- Risk Categories -->
            <div v-if="riskAnalysisStore.availableCategories?.length > 0">
              <h4 class="font-semibold text-gray-900 dark:text-white mb-3">Risk Categories</h4>
              <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                <div
                  v-for="category in riskAnalysisStore.availableCategories"
                  :key="category.id"
                  @click="openCategoryModal({ categoryName: category.name, categoryKey: category.id, findings: category.findings || [] })"
                  class="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 border border-gray-200 dark:border-gray-700 cursor-pointer hover:border-blue-500 dark:hover:border-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all group"
                >
                  <div class="flex items-center justify-between mb-2">
                    <h5 class="font-medium text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">{{ category.name }}</h5>
                    <span class="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-full text-xs font-medium">
                      {{ category.findingsCount || 0 }}
                    </span>
                  </div>
                  <p class="text-xs text-gray-600 dark:text-gray-400">Click to view findings</p>
                </div>
              </div>
            </div>
        </div>
        
        <!-- Loading State -->
        <div v-if="isLoading && !riskAnalysis" class="text-center py-12">
          <div class="inline-block w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-4"></div>
          <p class="text-gray-600 dark:text-gray-400">Generating risk analysis...</p>
          <p class="text-sm text-gray-500 dark:text-gray-500 mt-2">This may take a few minutes</p>
        </div>

        <!-- Error State -->
        <div v-if="error" class="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6 text-center">
          <div class="text-red-600 dark:text-red-400 mb-2">
            <svg class="w-12 h-12 mx-auto mb-4" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
            </svg>
          </div>
          <h3 class="font-semibold text-red-900 dark:text-red-100 mb-2">Analysis Error</h3>
          <p class="text-red-700 dark:text-red-300 mb-4">{{ error }}</p>
          <button @click="clearError" class="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg">
            Dismiss
          </button>
        </div>

        <!-- No Analysis State -->
        <div v-if="!riskAnalysis && !isLoading && !error" class="text-center py-12">
          <div class="text-gray-400 mb-4">
            <svg class="w-16 h-16 mx-auto" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zm0 4a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1V8zm8 0a1 1 0 011-1h4a1 1 0 011 1v2a1 1 0 01-1 1h-4a1 1 0 01-1-1V8z" clip-rule="evenodd" />
            </svg>
          </div>
          <h3 class="text-xl font-semibold text-gray-900 dark:text-white mb-2">No Risk Analysis Available</h3>
          <p class="text-gray-600 dark:text-gray-400 mb-6">Generate a comprehensive risk analysis to identify potential risks and opportunities.</p>
          <button
            @click="generateNewAnalysis"
            class="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium"
          >
            Generate Risk Analysis
          </button>
        </div>



        <!-- Metadata Modal -->
        <Teleport to="body">
          <Transition
            enter-active-class="transition-opacity duration-200 ease-out"
            enter-from-class="opacity-0"
            enter-to-class="opacity-100"
            leave-active-class="transition-opacity duration-150 ease-in"
            leave-from-class="opacity-100"
            leave-to-class="opacity-0"
          >
            <div
              v-if="showMetadataModal"
              class="fixed inset-0 bg-gray-900/50 backdrop-blur-sm flex items-center justify-center p-4 z-50"
              @click.self="showMetadataModal = false"
            >
              <Transition
                enter-active-class="transition-all duration-200 ease-out"
                enter-from-class="opacity-0 scale-95 translate-y-4"
                enter-to-class="opacity-100 scale-100 translate-y-0"
                leave-active-class="transition-all duration-150 ease-in"
                leave-from-class="opacity-100 scale-100 translate-y-0"
                leave-to-class="opacity-0 scale-95 translate-y-4"
              >
                <div
                  v-if="showMetadataModal"
                  class="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-2xl"
                  @click.stop
                >
                  <!-- Header -->
                  <div class="border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between">
                    <div class="flex items-center gap-3">
                      <div class="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                        <svg class="w-5 h-5 text-blue-600 dark:text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                          <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
                        </svg>
                      </div>
                      <div>
                        <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Analysis Details</h3>
                        <p class="text-sm text-gray-600 dark:text-gray-400">Metadata and data volume</p>
                      </div>
                    </div>
                    <button
                      @click="showMetadataModal = false"
                      class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                    >
                      <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  <!-- Body -->
                  <div class="px-6 py-4 space-y-6">
                    <!-- Entity Info -->
                    <div v-if="riskAnalysis?.metadata">
                      <h4 class="font-semibold text-gray-900 dark:text-white mb-3">Entity Information</h4>
                      <div class="grid grid-cols-2 gap-4">
                        <div>
                          <p class="text-sm text-gray-600 dark:text-gray-400">Entity Name</p>
                          <p class="font-medium text-gray-900 dark:text-white">{{ riskAnalysis.metadata.entityName }}</p>
                        </div>
                        <div>
                          <p class="text-sm text-gray-600 dark:text-gray-400">Type</p>
                          <p class="font-medium text-gray-900 dark:text-white capitalize">{{ riskAnalysis.metadata.entityType }}</p>
                        </div>
                        <div>
                          <p class="text-sm text-gray-600 dark:text-gray-400">Last Analyzed</p>
                          <p class="font-medium text-gray-900 dark:text-white">{{ formatDate(riskAnalysis.metadata.lastAnalyzed) }}</p>
                        </div>
                        <div>
                          <p class="text-sm text-gray-600 dark:text-gray-400">Phase</p>
                          <p class="font-medium text-gray-900 dark:text-white capitalize">{{ riskAnalysis.metadata.phase }}</p>
                        </div>
                      </div>
                    </div>

                    <!-- Data Volume -->
                    <div v-if="riskAnalysis?.metadata?.dataVolume">
                      <h4 class="font-semibold text-gray-900 dark:text-white mb-3">Data Volume</h4>
                      <div class="grid grid-cols-2 gap-4">
                        <div class="bg-gray-50 dark:bg-gray-900 rounded-lg p-3">
                          <p class="text-sm text-gray-600 dark:text-gray-400">Articles</p>
                          <p class="text-2xl font-bold text-gray-900 dark:text-white">{{ riskAnalysis.metadata.dataVolume.articles }}</p>
                        </div>
                        <div class="bg-gray-50 dark:bg-gray-900 rounded-lg p-3">
                          <p class="text-sm text-gray-600 dark:text-gray-400">Entity Records</p>
                          <p class="text-2xl font-bold text-gray-900 dark:text-white">{{ riskAnalysis.metadata.dataVolume.entityRecords }}</p>
                        </div>
                        <div class="bg-gray-50 dark:bg-gray-900 rounded-lg p-3">
                          <p class="text-sm text-gray-600 dark:text-gray-400">Documents</p>
                          <p class="text-2xl font-bold text-gray-900 dark:text-white">{{ riskAnalysis.metadata.dataVolume.documents }}</p>
                        </div>
                        <div class="bg-gray-50 dark:bg-gray-900 rounded-lg p-3">
                          <p class="text-sm text-gray-600 dark:text-gray-400">Total Clusters</p>
                          <p class="text-2xl font-bold text-gray-900 dark:text-white">{{ riskAnalysis.metadata.dataVolume.totalClusters }}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Footer -->
                  <div class="border-t border-gray-200 dark:border-gray-700 px-6 py-4 flex justify-end">
                    <button
                      @click="showMetadataModal = false"
                      class="px-4 py-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-medium transition-colors"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </Transition>
            </div>
          </Transition>
        </Teleport>
      </section>
    </main>

    <!-- Floating Agent Button -->
    <button
      @click="isAgentOpen = !isAgentOpen"
      class="fixed bottom-6 right-6 w-14 h-14 bg-blue-600 hover:bg-blue-700 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-200 z-50 flex items-center justify-center group"
      :class="{ 'scale-95': isAgentOpen }"
    >
      <!-- Close icon when open -->
      <svg v-if="isAgentOpen" class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
      </svg>
      <!-- AI Chat icon when closed -->
      <svg v-else class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
      </svg>
    </button>

    <!-- Agent Chat Overlay Panel -->
    <Transition
      enter-active-class="transition ease-out duration-200"
      enter-from-class="opacity-0 translate-y-4"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition ease-in duration-150"
      leave-from-class="opacity-100 translate-y-0"
      leave-to-class="opacity-0 translate-y-4"
    >
      <div
        v-if="isAgentOpen"
        ref="agentPanelRef"
        class="fixed bottom-24 right-6 w-[400px] max-h-[600px] bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 z-50 flex flex-col overflow-hidden"
      >
        <AgentChatSidebar
          :activeProject="session"
          :loading="isLoading"
          @close="isAgentOpen = false"
        />
      </div>
    </Transition>

    <!-- Category Detail Modal -->
    <Teleport to="body">
      <Transition
        enter-active-class="transition-opacity duration-200 ease-out"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="transition-opacity duration-150 ease-in"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <div
          v-if="showCategoryModal"
          class="fixed inset-0 bg-gray-900/50 backdrop-blur-sm flex items-center justify-center p-4 z-100"
          @click.self="closeCategoryModal"
        >
          <Transition
            enter-active-class="transition-all duration-200 ease-out"
            enter-from-class="opacity-0 scale-95 translate-y-4"
            enter-to-class="opacity-100 scale-100 translate-y-0"
            leave-active-class="transition-all duration-150 ease-in"
            leave-from-class="opacity-100 scale-100 translate-y-0"
            leave-to-class="opacity-0 scale-95 translate-y-4"
          >
            <div
              v-if="showCategoryModal"
              class="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col"
              @click.stop
            >
              <!-- Sticky Header -->
              <div class="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between rounded-t-xl z-10">
                <div class="flex items-center gap-3">
                  <div class="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                    <svg class="w-5 h-5 text-blue-600 dark:text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
                    </svg>
                  </div>
                  <div>
                    <h3 class="text-lg font-semibold text-gray-900 dark:text-white">{{ selectedCategoryData?.categoryName || 'Category Details' }}</h3>
                    <p class="text-sm text-gray-600 dark:text-gray-400">{{ selectedCategoryData?.findings?.length || 0 }} findings</p>
                  </div>
                </div>
                <button
                  @click="closeCategoryModal"
                  class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                >
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <!-- Scrollable Body -->
              <div class="flex-1 overflow-y-auto px-6 py-4" style="max-height: 70vh;">
                <div v-if="selectedCategoryData?.findings?.length > 0" class="space-y-4">
                  <RiskFindingCard
                    v-for="finding in selectedCategoryData.findings"
                    :key="finding.questionId"
                    :finding="{ ...finding, categoryName: selectedCategoryData.categoryName, categoryKey: selectedCategoryData.categoryKey }"
                  />
                </div>
                <div v-else class="text-center py-12">
                  <div class="text-green-400 mb-4">
                    <svg class="w-16 h-16 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                    </svg>
                  </div>
                  <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-2">No Findings in {{ selectedCategoryData?.categoryName }}</h3>
                  <p class="text-gray-600 dark:text-gray-400">
                    This category was analyzed and no risk factors were identified based on available data.
                  </p>
                  <p class="text-sm text-gray-500 dark:text-gray-500 mt-2">
                    This indicates neither risk nor reward unless more information comes to light.
                  </p>
                </div>
              </div>

              <!-- Sticky Footer -->
              <div class="sticky bottom-0 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 px-6 py-4 flex justify-end rounded-b-xl">
                <button
                  @click="closeCategoryModal"
                  class="px-4 py-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-medium transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </Transition>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import BreadCrumbs from '@/layouts/BreadCrumbs.vue'
import PhaseNavigation from '@/components/PhaseNavigation.vue'
import AgentChatSidebar from '@/components/AgentChatSidebar.vue'
import RiskLevelBadge from '@/components/RiskLevelBadge.vue'
import RiskFindingCard from '@/components/RiskFindingCard.vue'
import { useRiskAnalysisStore } from '@/stores/riskAnalysis'

const route = useRoute()
const router = useRouter()
const riskAnalysisStore = useRiskAnalysisStore()

// Route parameters as computed properties
const projectId = computed(() => route.query.projectId)
const sessionId = computed(() => route.query.sessionId)

// Agent chat overlay state
const isAgentOpen = ref(false)
const agentPanelRef = ref(null)

// Session context for agent
const session = computed(() => ({
  _id: sessionId.value,
  projectId: projectId.value,
  phase: 'analysis',
  riskAnalysis: riskAnalysis.value
}))

// State
const isLoading = ref(false)
const error = ref(null)
const showCategoryModal = ref(false)
const selectedCategoryData = ref(null)
const showMetadataModal = ref(false)

// Computed properties
const riskAnalysis = computed(() => riskAnalysisStore.riskAnalysis)

// Use the store's computed properties for the correct API structure
const overallRiskScore = computed(() => riskAnalysisStore.overallRiskScore)
const overallRiskLevel = computed(() => riskAnalysisStore.overallRiskLevel)
const totalFindings = computed(() => riskAnalysisStore.totalFindings)
const categoryCount = computed(() => riskAnalysisStore.availableCategories.length)

// Methods
const generateNewAnalysis = async () => {
  isLoading.value = true
  error.value = null
  
  try {
    if (!sessionId.value) {
      throw new Error('Session ID is required for risk analysis')
    }
    
    await riskAnalysisStore.generateRiskAnalysis(projectId.value, sessionId.value)
  } catch (err) {
    error.value = err.message || 'Failed to generate risk analysis'
  } finally {
    isLoading.value = false
  }
}

const reanalyzeRiskAnalysis = async () => {
  isLoading.value = true
  error.value = null
  
  try {
    if (!sessionId.value) {
      throw new Error('Session ID is required for risk analysis')
    }
    
    // Force regenerate the risk analysis with updated options
    await riskAnalysisStore.generateRiskAnalysis(projectId.value, sessionId.value, {
      forceRegenerate: true,
      analysisDepth: 'comprehensive',
      includeRecommendations: true
    })
  } catch (err) {
    error.value = err.message || 'Failed to re-analyze risk analysis'
  } finally {
    isLoading.value = false
  }
}

const clearError = () => {
  error.value = null
}

const openCategoryModal = (category) => {
  selectedCategoryData.value = category
  showCategoryModal.value = true
}

const closeCategoryModal = () => {
  showCategoryModal.value = false
  setTimeout(() => {
    selectedCategoryData.value = null
  }, 200)
}

const goToReport = () => {
  router.push({
    path: '/projects/report',
    query: {
      projectId: projectId.value,
      sessionId: sessionId.value
    }
  })
}

const formatDate = (dateString) => {
  if (!dateString) return 'Unknown'
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

// Click outside handler for agent panel
const handleClickOutsideAgent = (event) => {
  if (agentPanelRef.value && !agentPanelRef.value.contains(event.target) && !event.target.closest('button[class*="fixed bottom-6 right-6"]')) {
    isAgentOpen.value = false
  }
}

// Watch agent open state to add/remove click listener
watch(isAgentOpen, (newValue) => {
  if (newValue) {
    setTimeout(() => {
      document.addEventListener('click', handleClickOutsideAgent)
    }, 100)
  } else {
    document.removeEventListener('click', handleClickOutsideAgent)
  }
})

// Lifecycle
onMounted(async () => {
  if (sessionId.value) {
    // Try to fetch existing analysis first
    try {
      await riskAnalysisStore.getRiskAnalysis(projectId.value, sessionId.value)
    } catch (err) {
      // If no analysis exists, that's ok - user can generate one
      if (!err.message?.includes('404') && !err.message?.includes('not found')) {
        error.value = err.message || 'Failed to load existing risk analysis'
      }
    }
  }
})

// Cleanup click listener on unmount
onUnmounted(() => {
  document.removeEventListener('click', handleClickOutsideAgent)
})
</script>